import {
  Action,
  ThunkAction,
  combineReducers,
  configureStore,
} from "@reduxjs/toolkit"
import { setupListeners } from "@reduxjs/toolkit/query"
import { cloneDeep, isString } from "lodash"
import { createTransform, persistReducer, persistStore } from "redux-persist"
import storage from "redux-persist/lib/storage"
import { UUID } from "shared-ts/classes/uuid"

import { apiSlice } from "features/api/apiSlice"
import { authSlice, clearState } from "features/auth/authSlice"
import { cardReadersSlice } from "features/pos/cardReadersSlice"
import { countSlice } from "features/pos/counts/countSlice"
import { printersSlice } from "features/pos/printersSlice"
import { orderShipmentsSlice } from "features/pos/purchase-orders/orderShipmentsSlice"
import { ordersSlice } from "features/pos/purchase-orders/ordersSlice"
import { billsSlice } from "features/pos/receivables/billsSlice"
import { returnsSlice } from "features/pos/sales/returnsSlice"
import { salesSlice } from "features/pos/sales/salesSlice"
import { transfersSlice } from "features/pos/transfers/transfersSlice"
import { themeSlice } from "features/theme/themeSlice"

// transformInbound recursively searches the object to be persisted
// and provides workarounds for being able to serialize types that
// aren't natively serializable (e.g. bigint, Uint8Array). This
// currently assumes that the only Uint8Array fields in our state
// are UUIDs.
export function transformInbound(obj: unknown) {
  if (typeof obj !== "object" || obj === null) {
    return obj
  }

  const objRecord = obj as Record<string, unknown>
  for (const key in objRecord) {
    switch (typeof objRecord[key]) {
      case "bigint":
        objRecord[key] =
          `rundoo_bigint:${(objRecord[key] as bigint).toString()}`
        break
      case "object":
        if (objRecord[key] instanceof Uint8Array) {
          objRecord[key] =
            `rundoo_uuid:${UUID.fromBytes(objRecord[key]).toString()}`
          break
        }

        transformInbound(objRecord[key])
        break
      default:
    }
  }

  return objRecord
}

// transformOutbound recursively searches the persisted object
// and parses the non-natively-serializable types based on the
// workarounds implemented in `transformInbound` above.
export function transformOutbound(obj: unknown) {
  if (typeof obj !== "object" || obj === null) {
    return obj
  }

  const objRecord = obj as Record<string, unknown>
  for (const key in objRecord) {
    if (typeof objRecord[key] === "object") {
      transformOutbound(objRecord[key])
      continue
    }

    if (
      isString(objRecord[key]) &&
      (objRecord[key] as string).startsWith("rundoo_bigint:")
    ) {
      objRecord[key] = BigInt(
        (objRecord[key] as string).split("rundoo_bigint:")[1]
      )
      continue
    }

    if (
      isString(objRecord[key]) &&
      (objRecord[key] as string).startsWith("rundoo_uuid:")
    ) {
      objRecord[key] = UUID.fromString(
        (objRecord[key] as string).split("rundoo_uuid:")[1]
      ).toBytes()
      continue
    }
  }

  return objRecord
}

const Transform = createTransform(
  (inbound) => {
    const clone = cloneDeep(inbound)
    transformInbound(clone)
    return clone
  },
  (outbound) => {
    const clone = cloneDeep(outbound)
    transformOutbound(clone)
    return clone
  }
)

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  blacklist: [
    apiSlice.reducerPath,
    ordersSlice.name,
    orderShipmentsSlice.name,
    transfersSlice.name,
    salesSlice.name,
    returnsSlice.name,
    billsSlice.name,
    countSlice.name,
  ],
  transforms: [Transform],
}

const appReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  [themeSlice.name]: themeSlice.reducer,
  [authSlice.name]: authSlice.reducer,
  [billsSlice.name]: billsSlice.reducer,
  [countSlice.name]: countSlice.reducer,
  [ordersSlice.name]: ordersSlice.reducer,
  [orderShipmentsSlice.name]: orderShipmentsSlice.reducer,
  [transfersSlice.name]: transfersSlice.reducer,
  [salesSlice.name]: salesSlice.reducer,
  [returnsSlice.name]: returnsSlice.reducer,
  [cardReadersSlice.name]: cardReadersSlice.reducer,
  [printersSlice.name]: printersSlice.reducer,
})

const rootReducer = (state: any, action: any) => {
  if (clearState.match(action)) {
    state = {
      auth: state.auth,
      theme: state.theme,
      cardReaders: state.cardReaders,
      printers: state.printers,
    }
  }
  return appReducer(state, action)
}

const store = configureStore({
  reducer: persistReducer(persistConfig, rootReducer),
  devTools: {
    serialize: {
      replacer: (key: string, value: unknown) => {
        return typeof value === "bigint"
          ? { data: value.toString(), __serializedType__: "BigInt" }
          : value
      },
      reviver: (key: string, value: unknown) => {
        if (
          typeof value === "object" &&
          value !== null &&
          "__serializedType__" in value &&
          "data" in value
        ) {
          const data = value.data
          switch (value.__serializedType__) {
            case "BigInt":
              return BigInt(data as string)
            default:
              return data
          }
        }
      },
    },
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(apiSlice.middleware),
})

setupListeners(store.dispatch)

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof rootReducer>
export type AppState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  AppState,
  unknown,
  Action<string>
>

export default store
